import React, { useState } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
  RiShieldCheckFill,
  RiShieldLine,
  RiShieldCheckLine,
  RiKey2Line,
  RiFileCopyLine,
  RiCheckLine,
  RiDownload2Line,
  RiRefreshLine,
  RiSmartphoneLine,
  RiLockPasswordLine,
  RiPrinterLine,
  RiInformationLine,
  RiTimeLine,
  RiArrowRightLine,
} from 'react-icons/ri';
import {
  useGetMfaStatusQuery,
  useSetupMfaMutation,
  useEnableMfaMutation,
  useDisableMfaMutation,
  useRegenerateBackupCodesMutation,
} from 'actions/auth/mutations';
import OtpInput from './OtpInput';

const SecurityMFACard = () => {
  const { data: mfaStatus, isLoading: isStatusLoading, refetch } = useGetMfaStatusQuery();
  const [setupMfa, { isLoading: isSetupLoading }] = useSetupMfaMutation();
  const [enableMfa, { isLoading: isEnableLoading }] = useEnableMfaMutation();
  const [disableMfa, { isLoading: isDisableLoading }] = useDisableMfaMutation();
  const [regenerateBackupCodes, { isLoading: isRegenerateLoading }] = useRegenerateBackupCodesMutation();

  // Setup Modal States
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [setupData, setSetupData] = useState(null); // { secret, otpauth_url, qr_code }
  const [verificationCode, setVerificationCode] = useState('');
  const [generatedBackupCodes, setGeneratedBackupCodes] = useState(null);
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [copiedCodes, setCopiedCodes] = useState(false);

  // Disable Modal States
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [disableOtp, setDisableOtp] = useState('');

  // Regenerate Codes Modal States
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [regenPassword, setRegenPassword] = useState('');
  const [regenOtp, setRegenOtp] = useState('');

  const isMfaEnabled = Boolean(mfaStatus?.mfa_enabled);

  // Démarre la configuration MFA
  const handleStartSetup = async () => {
    try {
      const data = await setupMfa().unwrap();
      setSetupData(data);
      setVerificationCode('');
      setGeneratedBackupCodes(null);
      setCopiedSecret(false);
      setShowSetupModal(true);
    } catch (err) {
      toast.error(err?.data?.detail || "Impossible d'initialiser la configuration pour le moment.");
    }
  };

  // Validation du code à 6 chiffres
  const handleConfirmEnable = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!verificationCode || verificationCode.trim().length !== 6) {
      toast.error('Veuillez saisir le code complet à 6 chiffres affiché sur votre application.');
      return;
    }

    try {
      const res = await enableMfa({
        secret: setupData.secret,
        code: verificationCode.trim(),
      }).unwrap();

      toast.success(res.message || 'Double authentification activée avec succès !');
      setGeneratedBackupCodes(res.backup_codes);
      refetch();
    } catch (err) {
      toast.error(err?.data?.detail || 'Le code saisi est invalide ou a expiré. Veuillez réessayer.');
    }
  };

  // Désactivation du MFA
  const handleConfirmDisable = async (e) => {
    e.preventDefault();
    if (!disablePassword) {
      toast.error('Votre mot de passe est nécessaire pour confirmer la désactivation.');
      return;
    }

    try {
      const res = await disableMfa({
        password: disablePassword,
        code: disableOtp ? disableOtp.trim() : undefined,
      }).unwrap();

      toast.success(res.message || 'La double authentification a été désactivée.');
      setShowDisableModal(false);
      setDisablePassword('');
      setDisableOtp('');
      refetch();
    } catch (err) {
      toast.error(err?.data?.detail || 'Mot de passe incorrect ou erreur lors de la désactivation.');
    }
  };

  // Régénération des codes de secours
  const handleConfirmRegenerate = async (e) => {
    e.preventDefault();
    if (!regenPassword) {
      toast.error('Veuillez renseigner votre mot de passe actuel.');
      return;
    }

    try {
      const res = await regenerateBackupCodes({
        password: regenPassword,
        code: regenOtp ? regenOtp.trim() : undefined,
      }).unwrap();

      toast.success(res.message || 'Nouveaux codes de secours générés avec succès !');
      setGeneratedBackupCodes(res.backup_codes);
      setShowRegenerateModal(false);
      setRegenPassword('');
      setRegenOtp('');
      setShowSetupModal(true);
      refetch();
    } catch (err) {
      toast.error(err?.data?.detail || 'Mot de passe incorrect ou erreur lors de la régénération.');
    }
  };

  const handleCopySecret = () => {
    if (!setupData?.secret) return;
    navigator.clipboard.writeText(setupData.secret);
    setCopiedSecret(true);
    toast.info('Clé de configuration copiée dans le presse-papier !');
    setTimeout(() => setCopiedSecret(false), 2500);
  };

  const handleCopyAllCodes = (codes) => {
    if (!codes || !codes.length) return;
    navigator.clipboard.writeText(codes.join('\n'));
    setCopiedCodes(true);
    toast.info('Tous les codes de secours ont été copiés !');
    setTimeout(() => setCopiedCodes(false), 2500);
  };

  const downloadBackupCodes = (codes) => {
    const content =
      `EASY COMPTA : CODES DE SECOURS D'URGENCE (2FA / MFA)\n` +
      `Date de génération : ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}\n\n` +
      `IMPORTANT :\n` +
      `- Conservez ces codes en lieu sûr (gestionnaire de mots de passe ou coffre sécurisé).\n` +
      `- Chaque code n'est utilisable qu'une seule fois.\n` +
      `- Ils vous permettent de vous connecter si vous perdez l'accès à votre smartphone.\n\n` +
      `VOS CODES D'URGENCE :\n` +
      codes.map((c, i) => `  ${i + 1}. ${c}`).join('\n') +
      `\n\nhttps://easy-compta.bakeli.tech`;

    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `easycompta-codes-secours-${new Date().toISOString().slice(0, 10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Fichier des codes de secours téléchargé.');
  };

  const handlePrintCodes = () => {
    window.print();
  };

  return (
    <div
      className="card border-0 shadow-sm p-4"
      style={{
        borderRadius: '16px',
        backgroundColor: '#ffffff',
        border: '1px solid #eef2f6',
      }}
    >
      {/* ── EN-TÊTE DE LA CARTE ── */}
      <div className="d-flex align-items-start justify-content-between flex-wrap gap-3 pb-3 border-bottom">
        <div className="d-flex align-items-center gap-3">
          <div
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '14px',
              backgroundColor: isMfaEnabled ? '#ecfdf5' : '#f8fafc',
              border: isMfaEnabled ? '1px solid #a7f3d0' : '1px solid #e2e8f0',
              color: isMfaEnabled ? '#059669' : '#64748b',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px',
              flexShrink: 0,
            }}
          >
            {isMfaEnabled ? <RiShieldCheckFill /> : <RiShieldLine />}
          </div>
          <div>
            <h5 className="mb-1" style={{ fontWeight: 700, color: '#0f172a', fontSize: '18px' }}>
              Authentification multifacteur (2FA / MFA)
            </h5>
            <p className="mb-0" style={{ fontSize: '14px', color: '#64748b', lineHeight: 1.5 }}>
              Ajoutez une étape de vérification sur votre smartphone pour sécuriser l&apos;accès à vos dossiers comptables.
            </p>
          </div>
        </div>

        {/* Badge de statut */}
        <div>
          {isStatusLoading ? (
            <div className="d-flex align-items-center gap-2 text-muted" style={{ fontSize: '13px' }}>
              <Spinner animation="border" size="sm" variant="secondary" />
              <span>Vérification...</span>
            </div>
          ) : isMfaEnabled ? (
            <div
              className="d-inline-flex align-items-center gap-2 px-3 py-1"
              style={{
                backgroundColor: '#ecfdf5',
                color: '#065f46',
                border: '1px solid #a7f3d0',
                borderRadius: '9999px',
                fontSize: '13px',
                fontWeight: 600,
              }}
            >
              <span
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: '#10b981',
                  boxShadow: '0 0 0 2px #d1fae5',
                }}
              />
              Protection activée
            </div>
          ) : (
            <div
              className="d-inline-flex align-items-center gap-2 px-3 py-1"
              style={{
                backgroundColor: '#f1f5f9',
                color: '#475569',
                border: '1px solid #e2e8f0',
                borderRadius: '9999px',
                fontSize: '13px',
                fontWeight: 500,
              }}
            >
              Non configurée
            </div>
          )}
        </div>
      </div>

      {/* ── CORPS DE LA CARTE ── */}
      <div className="pt-3">
        {isMfaEnabled ? (
          <div>
            {/* Bannière active rassurante */}
            <div
              className="p-3 mb-3 d-flex align-items-center gap-3"
              style={{
                backgroundColor: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: '12px',
                color: '#166534',
              }}
            >
              <RiShieldCheckLine size={24} style={{ flexShrink: 0 }} />
              <div style={{ fontSize: '14px', lineHeight: 1.45 }}>
                <strong>Votre compte est sous haute protection.</strong> Lors de chaque connexion, un code dynamique TOTP à 6 chiffres généré par votre application mobile vous sera demandé après votre mot de passe.
              </div>
            </div>

            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 pt-2">
              <div className="d-flex align-items-center gap-2" style={{ fontSize: '14px', color: '#334155' }}>
                <RiKey2Line size={18} style={{ color: '#009688' }} />
                <span>
                  Codes de secours d’urgence restants :{' '}
                  <strong
                    style={{
                      color:
                        (mfaStatus?.backup_codes_count ?? 0) <= 2
                          ? '#dc2626'
                          : '#0f172a',
                    }}
                  >
                    {mfaStatus?.backup_codes_count ?? 0}
                  </strong>
                </span>
              </div>

              <div className="d-flex gap-2">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="rounded-pill px-3 py-2 d-inline-flex align-items-center gap-1"
                  style={{ fontSize: '13px', fontWeight: 500 }}
                  onClick={() => setShowRegenerateModal(true)}
                >
                  <RiRefreshLine /> Renouveler les codes
                </Button>
                <Button
                  variant="outline-danger"
                  size="sm"
                  className="rounded-pill px-3 py-2 d-inline-flex align-items-center gap-1"
                  style={{ fontSize: '13px', fontWeight: 500 }}
                  onClick={() => setShowDisableModal(true)}
                >
                  Désactiver
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div>
            <p style={{ fontSize: '14px', color: '#475569', lineHeight: 1.6, marginBottom: '16px' }}>
              La double authentification empêche toute connexion indésirable, même si quelqu&apos;un découvre votre mot de passe. Vous validerez vos connexions en quelques secondes à l&apos;aide de votre téléphone portable.
            </p>

            {/* 3 Avantages clés formulés humainement */}
            <div className="row g-3 mb-4">
              <div className="col-md-4">
                <div
                  className="p-3 h-100"
                  style={{
                    backgroundColor: '#f8fafc',
                    borderRadius: '12px',
                    border: '1px solid #f1f5f9',
                  }}
                >
                  <div className="d-flex align-items-center gap-2 mb-1" style={{ color: '#009688', fontWeight: 600, fontSize: '14px' }}>
                    <RiSmartphoneLine size={18} />
                    <span>Simple & Mobile</span>
                  </div>
                  <p className="mb-0 text-muted" style={{ fontSize: '13px' }}>
                    Compatible avec Google Authenticator, Microsoft Authenticator et 1Password.
                  </p>
                </div>
              </div>
              <div className="col-md-4">
                <div
                  className="p-3 h-100"
                  style={{
                    backgroundColor: '#f8fafc',
                    borderRadius: '12px',
                    border: '1px solid #f1f5f9',
                  }}
                >
                  <div className="d-flex align-items-center gap-2 mb-1" style={{ color: '#009688', fontWeight: 600, fontSize: '14px' }}>
                    <RiKey2Line size={18} />
                    <span>Codes de secours</span>
                  </div>
                  <p className="mb-0 text-muted" style={{ fontSize: '13px' }}>
                    10 codes d&apos;urgence imprimables pour débloquer votre accès en cas de perte de téléphone.
                  </p>
                </div>
              </div>
              <div className="col-md-4">
                <div
                  className="p-3 h-100"
                  style={{
                    backgroundColor: '#f8fafc',
                    borderRadius: '12px',
                    border: '1px solid #f1f5f9',
                  }}
                >
                  <div className="d-flex align-items-center gap-2 mb-1" style={{ color: '#009688', fontWeight: 600, fontSize: '14px' }}>
                    <RiTimeLine size={18} />
                    <span>Rapide à activer</span>
                  </div>
                  <p className="mb-0 text-muted" style={{ fontSize: '13px' }}>
                    Moins de 2 minutes suffisent : il vous suffit de scanner un QR Code.
                  </p>
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-end">
              <Button
                className="rounded-pill px-4 py-2 d-inline-flex align-items-center gap-2 shadow-sm"
                style={{
                  backgroundColor: '#009688',
                  borderColor: '#009688',
                  fontWeight: 600,
                  fontSize: '14px',
                }}
                disabled={isSetupLoading}
                onClick={handleStartSetup}
              >
                {isSetupLoading ? (
                  <>
                    <Spinner animation="border" size="sm" />
                    <span>Préparation du QR Code...</span>
                  </>
                ) : (
                  <>
                    <span>Configurer la double authentification</span>
                    <RiArrowRightLine size={16} />
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* ── MODAL 1 : CONFIGURATION (SCAN QR CODE & CODE OTP) ── */}
      <Modal
        show={showSetupModal}
        onHide={() => setShowSetupModal(false)}
        centered
        backdrop="static"
        size={generatedBackupCodes ? 'lg' : 'md'}
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a' }}>
            {generatedBackupCodes
              ? 'Sauvegarde de vos codes d’urgence'
              : 'Configuration en 2 étapes simples'}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="pt-2">
          {generatedBackupCodes ? (
            /* ── AFFICHAGE DES CODES DE SECOURS APRÈS ACTIVATION ── */
            <div>
              <div
                className="p-3 mb-3 d-flex align-items-center gap-3"
                style={{
                  backgroundColor: '#fef3c7',
                  border: '1px solid #fde68a',
                  borderRadius: '12px',
                  color: '#92400e',
                }}
              >
                <RiInformationLine size={24} style={{ flexShrink: 0 }} />
                <div style={{ fontSize: '13px', lineHeight: 1.5 }}>
                  <strong>Conservez ces codes précieusement.</strong> Ils ne seront affichés qu&apos;une seule fois. Si un jour vous n&apos;avez plus accès à votre téléphone, chaque code vous permettra de vous connecter une fois.
                </div>
              </div>

              {/* Grille des codes */}
              <div
                className="p-3 rounded mb-3"
                style={{
                  backgroundColor: '#f8fafc',
                  border: '1px dashed #cbd5e1',
                  borderRadius: '12px',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
                  gap: '10px',
                  textAlign: 'center',
                }}
              >
                {generatedBackupCodes.map((code, idx) => (
                  <div
                    key={idx}
                    className="p-2 bg-white rounded shadow-sm border"
                    style={{
                      fontFamily: 'monospace',
                      fontSize: '15px',
                      fontWeight: 700,
                      letterSpacing: '1px',
                      color: '#0f172a',
                    }}
                  >
                    {code}
                  </div>
                ))}
              </div>

              {/* Boutons d'action : Copier / Télécharger / Imprimer */}
              <div className="d-flex flex-wrap gap-2 justify-content-center mb-2">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="rounded-pill px-3 py-2 d-inline-flex align-items-center gap-1"
                  style={{ fontSize: '13px' }}
                  onClick={() => handleCopyAllCodes(generatedBackupCodes)}
                >
                  {copiedCodes ? <RiCheckLine style={{ color: '#059669' }} /> : <RiFileCopyLine />}
                  <span>{copiedCodes ? 'Copié !' : 'Copier tous les codes'}</span>
                </Button>
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="rounded-pill px-3 py-2 d-inline-flex align-items-center gap-1"
                  style={{ fontSize: '13px' }}
                  onClick={() => downloadBackupCodes(generatedBackupCodes)}
                >
                  <RiDownload2Line />
                  <span>Télécharger (.txt)</span>
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="rounded-pill px-3 py-2 d-inline-flex align-items-center gap-1"
                  style={{ fontSize: '13px' }}
                  onClick={handlePrintCodes}
                >
                  <RiPrinterLine />
                  <span>Imprimer</span>
                </Button>
              </div>
            </div>
          ) : (
            /* ── ÉTAPES DE CONFIGURATION QR CODE + SAISIE OTP ── */
            <div>
              {/* Étape 1 : Le QR Code */}
              <div className="mb-3">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <span
                    className="d-inline-flex align-items-center justify-content-center"
                    style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      backgroundColor: '#009688',
                      color: '#ffffff',
                      fontSize: '12px',
                      fontWeight: 700,
                    }}
                  >
                    1
                  </span>
                  <span style={{ fontWeight: 600, color: '#0f172a', fontSize: '14px' }}>
                    Scannez le QR Code avec votre application d&apos;authentification
                  </span>
                </div>

                {setupData?.qr_code && (
                  <div
                    className="text-center p-3 my-2 mx-auto"
                    style={{
                      backgroundColor: '#f8fafc',
                      borderRadius: '14px',
                      border: '1px solid #e2e8f0',
                      maxWidth: '260px',
                    }}
                  >
                    <img
                      src={setupData.qr_code}
                      alt="QR Code de configuration MFA"
                      style={{
                        width: '180px',
                        height: '180px',
                        objectFit: 'contain',
                        borderRadius: '8px',
                        backgroundColor: '#ffffff',
                        padding: '8px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                      }}
                    />

                    {/* Clé secrète de secours */}
                    <div className="mt-2 text-center">
                      <div className="text-muted" style={{ fontSize: '11px', marginBottom: '2px' }}>
                        Impossible de scanner ? Saisissez cette clé :
                      </div>
                      <div className="d-inline-flex align-items-center gap-2 px-2 py-1 bg-white rounded border">
                        <code style={{ fontSize: '12px', fontWeight: 700, color: '#0f172a' }}>
                          {setupData.secret}
                        </code>
                        <button
                          type="button"
                          className="btn btn-sm btn-link p-0 text-decoration-none d-inline-flex align-items-center gap-1"
                          style={{ color: '#009688', fontSize: '12px' }}
                          onClick={handleCopySecret}
                        >
                          {copiedSecret ? <RiCheckLine /> : <RiFileCopyLine />}
                          <span>{copiedSecret ? 'Copié' : 'Copier'}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Étape 2 : Saisie des 6 chiffres */}
              <div className="mt-4 pt-2 border-top">
                <div className="d-flex align-items-center gap-2 mb-1">
                  <span
                    className="d-inline-flex align-items-center justify-content-center"
                    style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      backgroundColor: '#009688',
                      color: '#ffffff',
                      fontSize: '12px',
                      fontWeight: 700,
                    }}
                  >
                    2
                  </span>
                  <span style={{ fontWeight: 600, color: '#0f172a', fontSize: '14px' }}>
                    Saisissez le code à 6 chiffres affiché par l’application
                  </span>
                </div>
                <p className="text-muted mb-2 ms-4" style={{ fontSize: '13px' }}>
                  Ce code change automatiquement toutes les 30 secondes.
                </p>

                <Form onSubmit={handleConfirmEnable}>
                  <OtpInput
                    value={verificationCode}
                    onChange={setVerificationCode}
                    onComplete={(code) => {
                      setVerificationCode(code);
                    }}
                    disabled={isEnableLoading}
                  />

                  <div className="d-flex justify-content-end gap-2 mt-4">
                    <Button
                      variant="light"
                      className="rounded-pill px-4"
                      style={{ fontSize: '14px', fontWeight: 500 }}
                      onClick={() => setShowSetupModal(false)}
                      disabled={isEnableLoading}
                    >
                      Annuler
                    </Button>
                    <Button
                      type="submit"
                      className="rounded-pill px-4 d-inline-flex align-items-center gap-2"
                      style={{
                        backgroundColor: '#009688',
                        borderColor: '#009688',
                        fontSize: '14px',
                        fontWeight: 600,
                      }}
                      disabled={isEnableLoading || verificationCode.trim().length !== 6}
                    >
                      {isEnableLoading ? (
                        <>
                          <Spinner animation="border" size="sm" />
                          <span>Validation...</span>
                        </>
                      ) : (
                        <span>Confirmer et activer</span>
                      )}
                    </Button>
                  </div>
                </Form>
              </div>
            </div>
          )}
        </Modal.Body>

        {generatedBackupCodes && (
          <Modal.Footer className="border-0 pt-0">
            <Button
              className="rounded-pill px-4 py-2 w-100"
              style={{
                backgroundColor: '#009688',
                borderColor: '#009688',
                fontWeight: 600,
                fontSize: '14px',
              }}
              onClick={() => {
                setShowSetupModal(false);
                setGeneratedBackupCodes(null);
              }}
            >
              J’ai bien mis mes codes en sécurité
            </Button>
          </Modal.Footer>
        )}
      </Modal>

      {/* ── MODAL 2 : DÉSACTIVATION MFA ── */}
      <Modal
        show={showDisableModal}
        onHide={() => setShowDisableModal(false)}
        centered
        size="md"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title style={{ fontSize: '17px', fontWeight: 700, color: '#0f172a' }}>
            Désactiver la double authentification
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleConfirmDisable}>
          <Modal.Body className="pt-2">
            <div
              className="p-3 mb-3 d-flex align-items-center gap-3"
              style={{
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '12px',
                color: '#991b1b',
              }}
            >
              <RiLockPasswordLine size={22} style={{ flexShrink: 0 }} />
              <div style={{ fontSize: '13px', lineHeight: 1.45 }}>
                Pour votre sécurité, confirmez votre mot de passe pour attester qu&apos;il s&apos;agit bien de vous.
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label style={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>
                Mot de passe de votre compte
              </Form.Label>
              <Form.Control
                type="password"
                required
                value={disablePassword}
                onChange={(e) => setDisablePassword(e.target.value)}
                placeholder="Entrez votre mot de passe habituel"
                autoFocus
                style={{
                  borderRadius: '10px',
                  padding: '10px 14px',
                  fontSize: '14px',
                }}
              />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label style={{ fontSize: '13px', fontWeight: 500, color: '#64748b' }}>
                Code TOTP ou code de secours (optionnel)
              </Form.Label>
              <Form.Control
                type="text"
                value={disableOtp}
                onChange={(e) => setDisableOtp(e.target.value)}
                placeholder="Ex : 123456"
                style={{
                  borderRadius: '10px',
                  padding: '10px 14px',
                  fontSize: '14px',
                }}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="border-0 pt-0">
            <Button
              variant="light"
              className="rounded-pill px-3"
              onClick={() => setShowDisableModal(false)}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              variant="danger"
              className="rounded-pill px-4"
              style={{ fontWeight: 600 }}
              disabled={isDisableLoading || !disablePassword}
            >
              {isDisableLoading ? <Spinner animation="border" size="sm" /> : 'Confirmer la désactivation'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* ── MODAL 3 : RENOUVELER LES CODES DE SECOURS ── */}
      <Modal
        show={showRegenerateModal}
        onHide={() => setShowRegenerateModal(false)}
        centered
        size="md"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title style={{ fontSize: '17px', fontWeight: 700, color: '#0f172a' }}>
            Renouveler vos codes de secours
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleConfirmRegenerate}>
          <Modal.Body className="pt-2">
            <p className="text-muted mb-3" style={{ fontSize: '13px', lineHeight: 1.5 }}>
              Cette action invalidera tous vos anciens codes d&apos;urgence et en générera 10 nouveaux.
            </p>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>
                Mot de passe de votre compte
              </Form.Label>
              <Form.Control
                type="password"
                required
                value={regenPassword}
                onChange={(e) => setRegenPassword(e.target.value)}
                placeholder="Votre mot de passe"
                autoFocus
                style={{
                  borderRadius: '10px',
                  padding: '10px 14px',
                  fontSize: '14px',
                }}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="border-0 pt-0">
            <Button
              variant="light"
              className="rounded-pill px-3"
              onClick={() => setShowRegenerateModal(false)}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              className="rounded-pill px-4"
              style={{ backgroundColor: '#009688', borderColor: '#009688', fontWeight: 600 }}
              disabled={isRegenerateLoading || !regenPassword}
            >
              {isRegenerateLoading ? <Spinner animation="border" size="sm" /> : 'Générer de nouveaux codes'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default SecurityMFACard;
